/**
 * Parse AAMVA-encoded PDF417 barcode data from US/Canadian driver's licenses.
 * Handles AAMVA versions 1–10 (2000–2020).
 *
 * Raw barcode structure:
 *   @ LF RS CR  ANSI <header> DL <subfile-data>
 *   Each data element: <3-char-ID><value>\n
 *   EXCEPT the first element in the DL subfile which is on the same line as "DL":
 *     e.g. "DLDAQV1234567\n"
 */
export function parseAamva(raw) {
  if (!raw || typeof raw !== 'string') return null;

  // Normalize all line-ending and AAMVA control characters to \n
  const text = raw
    .replace(/\r\n/g, '\n')
    .replace(/\r/g, '\n')
    // eslint-disable-next-line no-control-regex
    .replace(/\x1e/g, '\n') // RS (record separator)
    // eslint-disable-next-line no-control-regex
    .replace(/\x1c/g, '\n') // FS (file separator)
    // eslint-disable-next-line no-control-regex
    .replace(/\x0d/g, '\n'); // CR (used as segment terminator in some versions)

  const fields = {};

  for (const rawLine of text.split('\n')) {
    const line = rawLine.trim();
    if (!line || line.length < 3) continue;

    // Normal case: line begins with a 3-uppercase-letter element ID
    if (/^[A-Z]{3}/.test(line)) {
      const code = line.substring(0, 3);
      const value = line.substring(3).trim();
      if (value && !fields[code]) fields[code] = value;
      continue;
    }

    // Subfile-prefix case: line starts with "DL" or "ID" immediately followed by an element
    // e.g. "DLDAQV1234567" → code=DAQ, value=V1234567
    //      "IDDAQ12345678" → code=DAQ, value=12345678
    if (/^(?:DL|ID)[A-Z]{3}/.test(line)) {
      const code = line.substring(2, 5);
      const value = line.substring(5).trim();
      if (value && !fields[code]) fields[code] = value;
    }
  }

  // ── Name extraction ──────────────────────────────────────────────────────
  let firstName = '';
  let lastName = '';
  let middleName = '';

  if (fields['DCS'] || fields['DAC']) {
    // AAMVA 2002+ (most common): separate first/last/middle fields
    lastName = fields['DCS'] || '';
    firstName = fields['DAC'] || '';
    middleName = fields['DAD'] || '';
  } else if (fields['DAA']) {
    // AAMVA 2000 (legacy): single full-name field "LAST,FIRST,MIDDLE" or "LAST$FIRST$MIDDLE"
    const sep = fields['DAA'].includes(',') ? ',' : '$';
    const parts = fields['DAA'].split(sep);
    lastName = parts[0] || '';
    firstName = parts[1] || '';
    middleName = parts[2] || '';
  }

  // Remove trailing filler characters some states append (e.g. commas, spaces)
  const clean = (s) => s.replace(/[, ]+$/, '').replace(/\s+/g, ' ').trim();

  // Licenses are stored in ALL CAPS — convert to title case for display
  const toTitle = (s) =>
    clean(s)
      .toLowerCase()
      .replace(/\b\w/g, (c) => c.toUpperCase());

  return {
    firstName: toTitle(firstName),
    lastName: toTitle(lastName),
    middleName: toTitle(middleName),
    dateOfBirth: parseDobString(fields['DBB'] || fields['DAB'] || ''),
    licenseNumber: clean(fields['DAQ'] || ''),
    issuingState: clean(fields['DAJ'] || ''),
    rawSnapshot: raw.substring(0, 800)
  };
}

function parseDobString(s) {
  if (!s) return '';
  const digits = s.replace(/\D/g, '');
  if (digits.length < 8) return '';
  const chunk = digits.substring(0, 8);

  // AAMVA standard (all versions): MMDDYYYY
  const mm = chunk.substring(0, 2);
  const dd = chunk.substring(2, 4);
  const yyyy = chunk.substring(4, 8);
  const d = new Date(`${yyyy}-${mm}-${dd}`);
  if (!isNaN(d) && Number(yyyy) > 1900 && Number(yyyy) < 2100) {
    return d.toISOString().substring(0, 10);
  }

  // Fallback: YYYYMMDD (some non-US licenses)
  const yyyy2 = chunk.substring(0, 4);
  const mm2 = chunk.substring(4, 6);
  const dd2 = chunk.substring(6, 8);
  const d2 = new Date(`${yyyy2}-${mm2}-${dd2}`);
  if (!isNaN(d2) && Number(yyyy2) > 1900 && Number(yyyy2) < 2100) {
    return d2.toISOString().substring(0, 10);
  }

  return '';
}
